/**
 * Medical reference data — Scenarios
 *
 * Single source of truth for scenario target vitals, waveform morphology, and
 * airway mechanics. Pure data: no logic. Consumed by scenarioEngine.js and
 * validated by `npm run check:medical`.
 *
 * Each scenario: { name, description?, rhythm?, capnoMorphology?, vent?, vitals }
 *   vitals     — absolute target values the patient transitions toward
 *   vent       — { vte, ppeak, pplat } airway mechanics (respiratory scenarios)
 *   rhythm     — ECG morphology key (waveformEngine)
 *   capnoMorphology — capnogram shape key
 *   aid        — (EPIC scenarios only) cognitive-aid cards for the monitor
 */
import { EPIC_SCENARIOS } from './epicScenarios.js';
import { OB_SCENARIOS, OB_AID_ATTACH } from './obScenarios.js';
import { DYSHB_SCENARIOS } from './dyshemoglobinScenarios.js';

// Baseline airway mechanics — restored when a respiratory scenario is cleared.
// Only the three airway-mechanics keys are managed by scenarios; PEEP / FiO₂ /
// rate / agent stay under the instructor's manual control and are never reset.
export const BASELINE_VENT = { vte: 500, ppeak: 18, pplat: 15 };

export const SCENARIOS = {
  // Stanford Emergency Manual (EPIC) events — carry `aid` cognitive-aid cards.
  ...EPIC_SCENARIOS,
  // Additional obstetric emergencies (also carry aid + investigations).
  ...OB_SCENARIOS,
  // Dyshaemoglobinaemias — SpO₂ is misleading and unresponsive to FiO₂.
  ...DYSHB_SCENARIOS,
  normal: {
    name: 'Normal Baseline',
    description: 'All parameters within physiological limits. Use as a baseline reset between scenarios.',
    vent: { ...BASELINE_VENT },
    vitals: {
      HR: 72,
      SpO2: 99,
      NIBP_Sys: 120,
      NIBP_Dia: 80,
      EtCO2: 38,
      RR: 14,
      Temp: 36.8,
      ABP_Sys: 118,
      ABP_Dia: 76,
      CVP: 4,
    },
  },
  cardiacTamponade: {
    name: 'Cardiac Tamponade',
    description:
      'Obstructive shock from pericardial effusion — Beck’s triad (hypotension, ' +
      'muffled heart sounds, distended neck veins), narrow pulse pressure, pulsus ' +
      'paradoxus, electrical alternans. Definitive Rx: pericardiocentesis. Avoid ' +
      'hypovolaemia, bradycardia, and high positive-pressure ventilation.',
    // Compensated-but-shocked tamponade: sinus tachycardia, hypotension with a
    // narrow pulse pressure, and equalization of pressures → markedly elevated CVP.
    rhythm: 'tamponade',              // ECG electrical alternans + ABP pulsus paradoxus
    // Invasive findings (pulsus on the arterial trace, elevated CVP) are the
    // teaching point, so reveal those channels when the scenario is applied.
    channels: { abp: true, cvp: true },
    vitals: {
      HR: 130,                        // compensatory sinus tachycardia
      SpO2: 94,
      NIBP_Sys: 84,   NIBP_Dia: 68,   // hypotension + narrow pulse pressure (16)
      ABP_Sys:  82,   ABP_Dia:  66,
      EtCO2: 28,                      // low pulmonary blood flow from poor CO
      RR: 26,                         // tachypnoea
      Temp: 36.8,
      CVP: 20,                        // ↑↑ JVP — hallmark of tamponade
    },
    aid: [
      { title: 'Recognise — Beck’s triad', lines: [
        'Hypotension + muffled heart sounds + distended neck veins (↑JVP)',
        'Narrow pulse pressure; pulsus paradoxus > 10 mmHg on the a-line',
        'Sinus tachycardia; low-voltage ECG ± electrical alternans',
        'Deteriorates to PEA arrest — a reversible arrest cause (H’s & T’s)',
      ] },
      { title: 'Confirm', lines: [
        'POCUS: pericardial effusion + diastolic RV / RA collapse',
        'Plethoric non-collapsing IVC; septal bounce',
        'In peri-arrest, do NOT delay decompression for imaging',
      ] },
      { title: 'Temporise', lines: [
        'IV fluid bolus to support preload (transient bridge)',
        'Vasopressor (epinephrine) to maintain coronary perfusion',
        'AVOID hypovolaemia, bradycardia, high PEEP / positive-pressure',
        '  — all drop preload → cardiovascular collapse',
      ] },
      { title: 'Definitive', lines: [
        'Pericardiocentesis — subxiphoid, echo-guided; aspirate effusion',
        'Even 30–50 mL removed can restore cardiac output',
        'Surgical pericardial window / thoracotomy if recurrent or traumatic',
        'Treat the cause: trauma, uraemia, malignancy, post-cardiac surgery',
      ] },
    ],
  },
  bradycardia: {
    name: 'Bradycardia',
    description: 'SA node depression — HR 38, reduced CO, compensatory hypotension. Treat if haemodynamically unstable: atropine, pacing, or epinephrine.',
    vitals: {
      HR: 38,
      SpO2: 98,
      NIBP_Sys: 95,
      NIBP_Dia: 60,
      EtCO2: 35,
      RR: 10,
      Temp: 36.5,
      ABP_Sys: 100,
      ABP_Dia: 58,
      CVP: 3,
    },
  },
  tachycardia: {
    name: 'Tachycardia',
    description: 'Sinus tachycardia — HR 140, BP maintained. Increased myocardial O₂ demand. Identify and treat the underlying cause.',
    vitals: {
      HR: 140,
      SpO2: 96,
      NIBP_Sys: 135,
      NIBP_Dia: 88,
      EtCO2: 42,
      RR: 20,
      Temp: 37.2,
      ABP_Sys: 138,
      ABP_Dia: 90,
      CVP: 5,
    },
  },
  vf: {
    name: 'VF / Cardiac Arrest',
    description: 'Pulseless ventricular fibrillation — chaotic rhythm, zero cardiac output. Begin CPR immediately; defibrillate as soon as available.',
    rhythm: 'vf',
    vitals: {
      HR: 0,
      SpO2: 40,
      NIBP_Sys: 0,
      NIBP_Dia: 0,
      EtCO2: 0,
      RR: 0,
      Temp: 35.5,
      ABP_Sys: 0,
      ABP_Dia: 0,
      CVP: 0,
    },
  },
  hypoxia: {
    name: 'Hypoxia / Desaturation',
    description: 'SpO₂ 82% — V/Q mismatch, hypoventilation, or shunt. Compensatory tachycardia. Increase FiO₂ and assess airway/ventilation.',
    vitals: {
      HR: 125,
      SpO2: 82,
      NIBP_Sys: 108,
      NIBP_Dia: 70,
      EtCO2: 42,
      RR: 24,
      Temp: 36.8,
      ABP_Sys: 110,
      ABP_Dia: 72,
      CVP: 4,
    },
  },
  hypotension: {
    name: 'Hypotension',
    description: 'NIBP 70/40 with compensatory tachycardia. Assess for haemorrhage, distributive, or cardiogenic shock. IV fluids, vasopressors.',
    // Restore perfusion with fluids + vasopressors. Propofol (or other vasodilating
    // anaesthetics) deepens the hypotension — avoid in the shocked patient.
    correctInterventions: ['ivFluid', 'phenylephrine', 'ephedrine', 'norepinephrine', 'vasopressin'],
    contraindicated: ['propofol'],
    vitals: {
      HR: 120,
      SpO2: 94,
      NIBP_Sys: 70,
      NIBP_Dia: 40,
      EtCO2: 32,
      RR: 22,
      Temp: 36.5,
      ABP_Sys: 68,
      ABP_Dia: 38,
      CVP: 2,
    },
  },
  bronchospasm: {
    name: 'Bronchospasm / High EtCO₂',
    description: 'Expiratory obstruction — shark-fin capnogram, EtCO₂ 65, wheeze, ↑airway pressure. Salbutamol first-line; epinephrine for severe or refractory cases.',
    // Obstructed exhalation is the hallmark — shark-fin capnogram auto-applies
    capnoMorphology: 'obstructed',
    // ↑↑ airway resistance: Ppeak rises sharply, Pplat only mildly (preserved
    // compliance) → wide PIP–plateau gradient; air trapping ↓ delivered VTe.
    vent: { vte: 380, ppeak: 45, pplat: 22 },
    vitals: {
      HR: 110,
      SpO2: 91,
      NIBP_Sys: 135,
      NIBP_Dia: 85,
      EtCO2: 65,
      RR: 28,
      Temp: 37.1,
      ABP_Sys: 138,
      ABP_Dia: 88,
      CVP: 6,
    },
  },
  laryngospasmPartial: {
    name: 'Partial Laryngospasm',
    description: 'Partial glottic closure — turbulent airflow, obstructed shark-fin capnogram, SpO₂ falling. Deepen anaesthesia or small-dose suxamethonium.',
    capnoMorphology: 'obstructed',
    // Upper-airway obstruction: high resistance → ↑Ppeak, Pplat near-normal.
    vent: { vte: 350, ppeak: 40, pplat: 16 },
    // Spontaneous/mask patient — NOT intubated (intubation is the rescue).
    intubated: false,
    vitals: {
      HR: 115,
      SpO2: 88,
      NIBP_Sys: 105,
      NIBP_Dia: 65,
      EtCO2: 28,
      RR: 24,
      Temp: 36.8,
      ABP_Sys: 108,
      ABP_Dia: 67,
      CVP: 5,
    },
  },
  laryngospasmComplete: {
    name: 'Complete Laryngospasm',
    description: 'Complete glottic closure — no airflow, EtCO₂ flatline, SpO₂ dropping rapidly. Urgent suxamethonium; prepare for emergency surgical airway.',
    capnoMorphology: 'normal',
    // Cannot ventilate — pressure pegs high, almost no volume delivered.
    vent: { vte: 60, ppeak: 60, pplat: 55 },
    // Spontaneous/mask patient — NOT intubated (surgical airway is the rescue).
    intubated: false,
    vitals: {
      HR: 135,
      SpO2: 70,
      NIBP_Sys: 95,
      NIBP_Dia: 55,
      EtCO2: 0,
      RR: 0,
      Temp: 36.8,
      ABP_Sys: 98,
      ABP_Dia: 57,
      CVP: 3,
    },
  },
  amnioticFluidEmbolism: {
    name: 'Amniotic Fluid Embolism',
    description: 'Amniotic fluid in maternal circulation — massive dead-space: EtCO₂ 12 despite ventilation. Cardiovascular collapse, DIC, refractory bronchospasm. Call for help.',
    rhythm: 'tachycardia',
    capnoMorphology: 'normal',
    // Refractory bronchospasm + pulmonary oedema: BOTH resistance (↑Ppeak) and
    // compliance (↑Pplat) deranged; delivered VTe falls.
    vent: { vte: 350, ppeak: 44, pplat: 30 },
    vitals: {
      // Phase 1: acute pulmonary vasospasm + RV failure
      // Signature finding: EtCO₂ collapses to ≤15 despite active ventilation
      // (massive dead-space — lungs ventilated but no pulmonary blood flow)
      HR: 142,
      SpO2: 72,
      NIBP_Sys: 55,
      NIBP_Dia: 30,
      EtCO2: 12,
      RR: 22,
      Temp: 37.1,
      ABP_Sys: 52,
      ABP_Dia: 28,
      CVP: 14,        // elevated — blood backing up from RV failure
    },
  },
  airEmbolism: {
    name: 'Air Embolism',
    description: 'Venous air entrainment — EtCO₂ sudden drop, HR ↑↑, haemodynamic collapse. Durant\'s manoeuvre (L lateral decubitus), 100% O₂, aspirate CVL.',
    // 100% O₂ (stop N₂O), flood/Durant position, aspirate air via CVC, support with
    // fluids + vasopressors.
    correctInterventions: ['nonRebreather', 'ivFluid', 'norepinephrine', 'epinephrine'],
    vitals: {
      HR: 155,
      SpO2: 88,
      NIBP_Sys: 75,
      NIBP_Dia: 45,
      EtCO2: 18,
      RR: 26,
      Temp: 36.7,
      ABP_Sys: 72,
      ABP_Dia: 42,
      CVP: 1,
    },
  },
  vtach: {
    name: 'Monomorphic VT',
    description: 'Wide-complex monomorphic VTach — HR 180, haemodynamically unstable. Amiodarone 300 mg IV or synchronised cardioversion. If pulseless → defibrillate.',
    rhythm: 'vtach',
    vitals: {
      HR: 180,
      SpO2: 85,
      NIBP_Sys: 75,
      NIBP_Dia: 50,
      EtCO2: 28,
      RR: 22,
      Temp: 37.0,
      ABP_Sys: 72,
      ABP_Dia: 48,
      CVP: 8,
    },
  },
  tdp: {
    name: 'Torsades de Pointes',
    description:
      'Polymorphic VT (TdP) — HR 220, QT-prolongation driven. ' +
      'First-line: IV Magnesium 2 g. Correct hypokalemia. ' +
      'Bradycardia-dependent or refractory → TCP overdrive pacing 100–120 bpm. ' +
      'Pulseless → Defibrillate (do NOT sync — polymorphic QRS prevents R-wave tracking).',
    rhythm: 'tdp',
    vitals: {
      HR:        220,
      SpO2:       80,
      NIBP_Sys:   68,   NIBP_Dia:  42,
      ABP_Sys:    65,   ABP_Dia:   40,
      EtCO2:      22,
      RR:         24,
      Temp:       37.0,
      CVP:         7,
    },
  },

  rapidAfibHypotension: {
    name: 'Rapid AFib / Hypotension',
    description: 'Rapid AFib RVR — loss of atrial kick + tachycardia → ↓CO → hypotension. Rate control (metoprolol, diltiazem) or synchronised DCCV if unstable.',
    // Rapid ventricular response in AFib (~130 bpm) with haemodynamic compromise.
    // Loss of atrial kick (~20% of cardiac output) + tachycardia → ↓CO → hypotension.
    rhythm: 'afib',
    vitals: {
      HR: 130,
      SpO2: 93,
      NIBP_Sys: 78,   NIBP_Dia: 48,
      ABP_Sys:  75,   ABP_Dia:  45,
      EtCO2: 32,
      RR: 22,
      Temp: 37.0,
      CVP: 6,
    },
  },

  rapidSVTHypotension: {
    name: 'SVT / Hypotension',
    description: 'SVT — narrow QRS, HR 190, no P waves, with haemodynamic compromise. UNSTABLE → immediate synchronised cardioversion. Adenosine only if regular & narrow-complex and it does not delay DCCV.',
    // Unstable SVT: synchronised cardioversion is first-line; adenosine is at most a
    // non-delaying adjunct for a regular narrow-complex rhythm.
    correctInterventions: ['synchronizedCardioversion', 'adenosine'],
    // Supraventricular tachycardia (~190 bpm) causing haemodynamic compromise.
    // Narrow QRS, no visible P waves; extremely short diastolic filling time → ↓CO.
    rhythm: 'svt',
    vitals: {
      HR: 190,
      SpO2: 92,
      NIBP_Sys: 82,   NIBP_Dia: 50,
      ABP_Sys:  80,   ABP_Dia:  48,
      EtCO2: 30,
      RR: 24,
      Temp: 37.1,
      CVP: 7,
    },
  },

  stDepression: {
    name: 'ST Depression',
    description: 'Subendocardial ischaemia — ST depression ≥1 mm, tachycardia, hypertension. 12-lead ECG, troponin, ACS protocol. Consider beta-blocker, nitrates.',
    rhythm: 'st_depression',
    vitals: {
      HR: 95,
      SpO2: 96,
      NIBP_Sys: 155,
      NIBP_Dia: 95,
      EtCO2: 36,
      RR: 18,
      Temp: 36.8,
      ABP_Sys: 158,
      ABP_Dia: 98,
      CVP: 6,
    },
  },
  stElevation: {
    name: 'ST Elevation (STEMI)',
    description: 'STEMI — culprit coronary occlusion, ST elevation in Lead II. Activate cath lab. Aspirin 300 mg, heparin, urgent PCI. Time is myocardium.',
    rhythm: 'st_elevation',
    vitals: {
      HR: 88,
      SpO2: 94,
      NIBP_Sys: 100,
      NIBP_Dia: 65,
      EtCO2: 34,
      RR: 20,
      Temp: 36.6,
      ABP_Sys: 98,
      ABP_Dia: 62,
      CVP: 7,
    },
  },
  rcaMI: {
    name: 'RCA MI / RV Infarction',
    description: 'RCA occlusion — inferior STEMI + RV infarction. Classic triad: bradycardia + hypotension + elevated CVP. IV fluids first-line. ⚠ NTG ABSOLUTELY CONTRAINDICATED.',
    // RV infarct is PRELOAD-DEPENDENT: fluids + inotropy + AV synchrony/rate support.
    // Nitrates/diuretics/opioids drop preload → catastrophic hypotension (avoid).
    correctInterventions: ['ivFluid', 'dobutamine', 'epinephrine', 'atropine', 'transcutaneousPacemaker'],
    contraindicated: ['nitroglycerin', 'nitroprusside', 'furosemide', 'morphine'],
    // RCA occlusion → inferior STEMI in Lead II + SA/AV node ischaemia → bradycardia
    // rhythm: 'st_elevation' tiled at HR=48 naturally produces slow broad STEMI complexes
    rhythm: 'st_elevation',
    capnoMorphology: 'normal',
    vitals: {
      HR: 48,         // SA node ischaemia (RCA supplies SA node in ~60% of population)
      SpO2: 93,       // mild desaturation — lungs are CLEAR (no pulmonary oedema)
      NIBP_Sys: 82,   // hypotension — LV starved of preload from failing RV
      NIBP_Dia: 52,
      EtCO2: 28,      // low CO but not catastrophic (cf. AFE at 12)
      RR: 18,
      Temp: 36.9,
      ABP_Sys: 80,
      ABP_Dia: 50,
      CVP: 14,        // elevated — venous congestion backing up from RV (JVD equivalent)
    },
  },

  increasedICP: {
    name: 'Increased ICP',
    description: 'Cushing\'s triad: ↑↑BP + bradycardia + irregular RR. Wide pulse pressure fingerprint of Cushing reflex. Mannitol, head elevation 30°, neurosurgical emergency.',
    // Lower the ICP (osmotherapy + brief hyperventilation) — the Cushing hypertension
    // then resolves. Do NOT directly vasodilate: nitroprusside raises ICP / drops CPP.
    correctInterventions: ['mannitol', 'hypertonicSaline', 'hyperventilation'],
    contraindicated: ['nitroprusside'],
    // Cushing's triad: hypertension + bradycardia + irregular respirations.
    // The pulse pressure is intentionally very wide (systolic ↑↑, diastolic
    // preserved) — this asymmetry is the haemodynamic fingerprint of the
    // Cushing reflex and distinguishes it from other causes of hypertension.
    vitals: {
      // Profound vagal bradycardia triggered by baroreceptors responding
      // to the massive sympathetic-driven systolic pressure surge.
      HR: 40,
      // Systolic driven very high to force CPP = MAP - ICP above critical
      // threshold; diastolic rises much less → wide pulse pressure (~126 mmHg).
      NIBP_Sys: 188,  NIBP_Dia: 62,
      ABP_Sys:  190,  ABP_Dia:  64,
      // Cheyne-Stokes / Biot's pattern: brainstem compression disrupts
      // respiratory drive → slow, irregular breaths with apnoeic episodes.
      RR: 5,
      // Hypoventilation during apnoeic phases retains CO₂.
      EtCO2: 54,
      // V/Q mismatch from hypoventilation + neurogenic pulmonary oedema risk.
      SpO2: 91,
      // Mild central hyperthermia from hypothalamic involvement.
      Temp: 38.2,
      // Elevated from sympathetic venoconstriction.
      CVP: 9,
    },
  },

  hypothermia: {
    name: 'Severe Hypothermia',
    description: 'Core temp 26°C — atropine-resistant bradycardia, Osborn J waves, near-apnoea. Warm IV fluids, external rewarming. ECMO if refractory VF.',
    // Osborn (J) waves + profound bradycardia + prolonged intervals.
    // Core temperature < 28 °C — all compensatory mechanisms have failed.
    rhythm: 'hypothermia',
    vitals: {
      // Profound sinus bradycardia — direct SA node cooling; atropine-resistant
      HR: 28,
      // Peripheral perfusion failure — pulse oximetry unreliable at this temp;
      // value reflects SpO2 obtained by ABG, not pleth (pleth waveform will be flat)
      SpO2: 60,
      // Profound hypotension — ↓ cardiac output + complete loss of vasomotor tone
      NIBP_Sys: 58,  NIBP_Dia: 28,
      ABP_Sys:  55,  ABP_Dia:  25,
      // Very low EtCO2 — profoundly depressed metabolism produces little CO2;
      // near-apneic respiratory pattern means very little gas is exhaled per minute
      EtCO2: 16,
      // Near-apnea — brainstem respiratory drive severely suppressed below 28 °C
      RR: 3,
      // Core temperature: severe / profound threshold (normal = 36.5–37.5 °C)
      Temp: 26.0,
      // Reduced venous return; cardiac output near-absent
      CVP: 2,
    },
  },

  hyperkalemia: {
    name: 'Hyperkalemia',
    description: 'Severe hyperkalaemia — peaked T waves, wide QRS, absent P waves, bradycardia. Calcium gluconate (membrane stabilise) → insulin/glucose → salbutamol → elimination.',
    // Membrane stabilise (calcium) → shift K⁺ (insulin+D50, bicarb, salbutamol) →
    // eliminate (furosemide). Succinylcholine is CONTRAINDICATED — it raises K⁺ further.
    correctInterventions: ['calciumChloride', 'insulinD50', 'sodiumBicarb', 'albuterol', 'furosemide'],
    contraindicated: ['succinylcholine'],
    // Wide QRS + absent P wave + peaked T — the ECG is the primary diagnostic tool.
    // Rhythm key triggers the Gaussian hyperkalemia waveform generator.
    rhythm: 'hyperkalemia',
    vitals: {
      // Bradycardia: SA and AV node suppression from elevated extracellular K⁺
      HR: 48,
      // Mild desaturation: reduced cardiac output + poor peripheral perfusion
      SpO2: 95,
      // Hypotension: impaired myocardial contractility + peripheral vasodilation
      NIBP_Sys: 86,  NIBP_Dia: 52,
      ABP_Sys:  84,  ABP_Dia:  50,
      // EtCO₂ relatively preserved early; mild rise from reduced cardiac output
      EtCO2: 40,
      // Mild tachypnoea: compensatory response to reduced cardiac output
      RR: 16,
      // Temperature: normal (not a feature of hyperkalemia itself)
      Temp: 36.7,
      // CVP mildly elevated: reduced RV output with preserved venous return
      CVP: 6,
    },
  },

  hyperkalemicArrest: {
    name: 'Hyperkalemic Arrest',
    description:
      'Severe hyperkalaemia degenerated to a sine-wave PEA arrest — broad, slurred complexes merging P-QRS-T, ' +
      'no cardiac output. REFRACTORY to defibrillation and adrenaline until K⁺ is addressed: calcium (membrane ' +
      'stabilisation, works within minutes) → insulin + D50 / sodium bicarbonate / salbutamol to shift K⁺ ' +
      'intracellularly → then standard ACLS can restore a perfusing rhythm.',
    rhythm: 'hyperkalemia',           // wide sine-wave complexes
    correctInterventions: ['calciumChloride', 'insulinD50', 'sodiumBicarb', 'albuterol', 'dialysis'],
    contraindicated: ['succinylcholine'],
    // Standard ACLS is futile until the potassium is shifted; calcium/insulin/
    // bicarb/salbutamol open the gate, after which defib can achieve ROSC.
    recoveryGate: {
      antidotes: ['calciumChloride', 'insulinD50', 'sodiumBicarb', 'albuterol', 'dialysis'],
      mode: 'any',
      settleMs: 30000,
      blocks: ['defibrillation', 'synchronizedCardioversion', 'epinephrine', 'norepinephrine', 'vasopressin', 'dopamine', 'atropine'],
      hint: 'Refractory to defibrillation/adrenaline until K⁺ is shifted: calcium → insulin+D50 / bicarbonate / salbutamol.',
    },
    vitals: {
      HR: 40,                         // slow, wide-complex electrical activity
      SpO2: 40,                       // arrest — pulse oximeter unreliable (near-flat pleth)
      NIBP_Sys: 0,   NIBP_Dia: 0,     // PEA — no mechanical output
      ABP_Sys:  0,   ABP_Dia:  0,
      EtCO2: 10,                      // minimal pulmonary blood flow
      RR: 0,
      Temp: 36.6,
      CVP: 12,
    },
  },

  synchronizedCardioversion: {
    name: 'Synchronized Cardioversion',
    // Successful DCCV of an unstable tachyarrhythmia (VTach with pulse, SVT, AFib/RVR).
    // The shock is R-wave-synchronised to avoid triggering VF.
    // Post-conversion: sinus rhythm restored; vitals recover from the tachycardia state.
    rhythm: 'normal',
    vitals: {
      HR: 78,
      SpO2: 97,
      NIBP_Sys: 108,  NIBP_Dia: 68,
      ABP_Sys:  105,  ABP_Dia:  65,
      EtCO2: 36,
      RR: 16,
      Temp: 36.9,
      CVP: 5,
    },
  },

  defibrillation: {
    name: 'Defibrillation',
    // Successful unsynchronised DC shock for pulseless VF / VTach.
    // Post-ROSC physiology: sinus rhythm restores, but cardiac stunning
    // leaves BP depressed and SpO₂ still recovering.
    rhythm: 'normal',
    vitals: {
      HR: 92,
      SpO2: 93,
      NIBP_Sys: 88,   NIBP_Dia: 55,
      ABP_Sys:  85,   ABP_Dia:  52,
      EtCO2: 32,
      RR: 20,
      Temp: 35.8,
      CVP: 3,
    },
  },

  last: {
    name: 'LA Systemic Toxicity',
    description:
      'LAST — Phase 2 cardiovascular collapse. Bupivacaine blocks cardiac Na⁺ channels → VT, profound hypotension, apnea. ' +
      'AVOID standard ACLS epinephrine doses and lidocaine. ' +
      'TREATMENT: 20% Intralipid bolus (lipid sink) → drains LA from cardiac tissue. Amiodarone for refractory VT.',
    rhythm: 'vtach',
    // ASRA/NYSORA LAST management. When an explicit list is present the preview
    // uses it instead of "any drug with a LAST-specific effect" (which would wrongly
    // surface the contraindicated drugs authored to demonstrate harm).
    //   Correct: 20% lipid emulsion (definitive), benzodiazepine for seizures,
    //   amiodarone for ventricular arrhythmia, REDUCED-dose epinephrine (≤1 µg/kg),
    //   airway/100% O₂.  Avoid: local anaesthetics (lidocaine), β-blockers, calcium
    //   channel blockers, vasopressin, and propofol (not a lipid substitute).
    correctInterventions: ['intralipid', 'midazolam', 'amiodarone', 'epinephrine', 'nonRebreather', 'intubation'],
    contraindicated: ['lidocaine', 'esmolol', 'labetalol', 'diltiazem', 'vasopressin', 'propofol'],
    // Refractory to ACLS: shocks and pressors do nothing until lipid emulsion has
    // been given and allowed to circulate (the "lipid sink" drains bupivacaine
    // from cardiac Na⁺ channels). See recoveryGate handling in InterventionPanel.
    recoveryGate: {
      antidotes: ['intralipid'],
      mode: 'any',
      settleMs: 45000,
      blocks: ['defibrillation', 'synchronizedCardioversion', 'epinephrine', 'norepinephrine', 'vasopressin', 'dopamine'],
      hint: 'Refractory to ACLS. Give 20% lipid emulsion (Intralipid) and let it circulate before shocks or pressors will restore output.',
    },
    vitals: {
      HR:        160,   // monomorphic VT — direct Na⁺ channel blockade
      SpO2:       72,   // apnea + catastrophic ↓CO → severe hypoxia
      NIBP_Sys:   52,   NIBP_Dia:  28,   // near-absent cardiac output
      ABP_Sys:    50,   ABP_Dia:   26,
      EtCO2:       8,   // near-flat: respiratory arrest + essentially zero pulmonary blood flow
      RR:          0,   // CNS depression → apnea
      Temp:       36.8,
      CVP:         1,   // empty tank — no effective venous return
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // OB/GYN — Obstetric Emergency Scenarios
  // Normal obstetric physiology: HR 80–100, NIBP 90–140/60–90, SpO₂ 98–100%,
  // EtCO₂ 28–32 (physiological hyperventilation of pregnancy), RR 16–20.
  // These scenarios reflect DEVIATION from those obstetric baselines.
  // Note: amnioticFluidEmbolism and last are defined in periop but fully
  // applicable here — they are referenced directly in the OB/GYN tab columns.
  // ═══════════════════════════════════════════════════════════════════════

  obgynHemorrhagicShock: {
    name: 'Hemorrhagic Shock',
    description:
      'Obstetric haemorrhage (PPH / placenta praevia / abruption) — massive blood loss. ' +
      'Class III–IV shock: HR 145, BP 72/42, Temp falling, metabolic acidosis. ' +
      'Activate MTP. IV/IO access × 2, 1:1:1 packed RBC:FFP:platelets. ' +
      'Oxytocin, tranexamic acid, uterotonic agents. Surgical control.',
    vitals: {
      HR: 145, SpO2: 90,
      NIBP_Sys: 72,  NIBP_Dia: 42,
      ABP_Sys:  70,  ABP_Dia:  40,
      EtCO2: 22,   // low CO + lactic acidosis drives compensatory hyperventilation
      RR: 32,
      Temp: 35.8,  // hypothermia from massive haemorrhage
      CVP: 1,      // empty tank — near-zero preload
    },
  },

  obgynHighSpinal: {
    name: 'High / Total Spinal',
    description:
      'Total spinal — LA spreads to C3–C5 (phrenic nerve) → apnoea; T1–T4 block → cardiac sympathectomy. ' +
      'Bezold-Jarisch reflex: paradoxical bradycardia from sudden RV preload loss. ' +
      'SpO₂ falling from apnoea; EtCO₂ → 0 (no ventilation to sample). ' +
      'TREATMENT: call for help, immediately bag-mask ventilate 100% O₂, ephedrine or phenylephrine, ' +
      'atropine for bradycardia, left uterine displacement, prepare for intubation.',
    vitals: {
      HR: 38,   SpO2: 62,           // paradoxical bradycardia + rapid desaturation
      NIBP_Sys: 48,  NIBP_Dia: 28,  // complete sympathectomy → profound vasodilation
      ABP_Sys:  45,  ABP_Dia:  25,
      EtCO2: 4,                     // near-zero — no breath to exhale CO₂ through
      RR: 2,                        // phrenic nerve blocked → near-apnoea
      Temp: 37.2,                   // warm, flushed from sympathetic block
      CVP: 1,                       // venous pooling from venodilation
    },
  },

  obgynEclampsia: {
    name: 'Severe Eclampsia',
    description:
      'Eclamptic seizure in severe preeclampsia — hypertensive crisis + post-ictal respiratory depression. ' +
      'CLASSIC: BP 188/122 + SpO₂ 86% + RR 8 + EtCO₂ 52 (CO₂ retention post-ictal). ' +
      'TREATMENT: MgSO₄ 4–6 g IV loading dose (seizure prophylaxis/termination), ' +
      'labetalol or hydralazine (target SBP < 160 to prevent stroke), ' +
      'airway management + O₂ + left lateral tilt. Delivery once stabilised.',
    vitals: {
      HR: 118, SpO2: 86,
      NIBP_Sys: 188, NIBP_Dia: 122,   // severe hypertensive crisis
      ABP_Sys:  185, ABP_Dia:  120,
      EtCO2: 52,     // CO₂ retention — post-ictal hypoventilation
      RR: 8,         // post-ictal respiratory depression
      Temp: 37.8,    // mild hyperthermia from seizure activity
      CVP: 7,        // hypervolaemia of preeclampsia
    },
  },

  obgynSCAD: {
    name: 'Coronary Artery Dissection',
    description:
      'Peripartum SCAD (spontaneous coronary artery dissection) — most common cause of ACS in pregnancy. ' +
      'LAD territory most affected; presents identically to atherosclerotic STEMI on ECG. ' +
      'ST elevation + flash pulmonary oedema from acute LV failure. ' +
      'Avoid thrombolytics (haemorrhage risk). Urgent cardiology — PCI vs conservative. ' +
      'Treat like STEMI: aspirin, heparin, supplemental O₂, manage pulmonary oedema.',
    rhythm: 'st_elevation',
    vitals: {
      HR: 108, SpO2: 88,
      NIBP_Sys: 88,  NIBP_Dia: 55,    // cardiogenic shock from acute LV failure
      ABP_Sys:  86,  ABP_Dia:  52,
      EtCO2: 30,     // low CO + pulmonary oedema shunt
      RR: 30,        // severe dyspnoea from flash pulmonary oedema
      Temp: 36.8,
      CVP: 12,       // elevated — LV failure → rising LVEDP transmitted backward
    },
  },

  obgynCardiacArrest: {
    name: 'Cardiac Arrest',
    // Maternal ACLS with manual left-uterine displacement; epinephrine, fluids;
    // perimortem C-section by ~4 min if no ROSC (procedural, see aid).
    correctInterventions: ['epinephrine', 'ivFluid'],
    description:
      'Obstetric cardiac arrest — shockable VF (most instructional initial presentation). ' +
      'OB MODIFICATIONS to ACLS: (1) Manual LEFT UTERINE DISPLACEMENT throughout CPR. ' +
      '(2) Perimortem C-section within 4–5 min of arrest if not achieving ROSC — ' +
      'improves maternal CPR effectiveness by relieving aortocaval compression. ' +
      '(3) Defibrillation energy unchanged. (4) Reverse treatable causes: AFE, LAST, eclampsia, high spinal.',
    rhythm: 'vf',
    vitals: {
      HR: 0,   SpO2: 40,
      NIBP_Sys: 0,  NIBP_Dia: 0,
      ABP_Sys:  0,  ABP_Dia:  0,
      EtCO2: 0, RR: 0,
      Temp: 36.0,  CVP: 0,
    },
  },

  obgynPeripartumCM: {
    name: 'Peripartum Cardiomyopathy',
    description:
      'PPCM with severe pulmonary oedema — idiopathic dilated CMP (EF < 45%) in last month or 5 months postpartum. ' +
      'SpO₂ 78% refractory to O₂ (shunt from alveolar flooding), CVP 18 (biventricular failure). ' +
      'TREATMENT: CPAP/HFNC → intubation if deteriorating. ' +
      'Cautious diuresis (furosemide), hydralazine/nitrates (vasodilation — avoid ACEi if pregnant), ' +
      'beta-blocker (carvedilol). ECMO for refractory shock. Bromocriptine may halt progression.',
    vitals: {
      HR: 128, SpO2: 78,            // biventricular failure → refractory hypoxia
      NIBP_Sys: 80,  NIBP_Dia: 52,  // cardiogenic shock
      ABP_Sys:  78,  ABP_Dia:  50,
      EtCO2: 26,     // very low CO (severe biventricular failure) + metabolic acidosis
      RR: 42,        // severe tachypnoea from alveolar flooding
      Temp: 36.5,
      CVP: 18,       // severely elevated — biventricular failure + fluid overload
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Endocrine Crises
  // ═══════════════════════════════════════════════════════════════════════

  pheoCrisis: {
    name: 'Pheochromocytoma Crisis',
    description:
      'Catecholamine surge — paroxysmal severe hypertension (240/130), tachycardia, sweating, headache. ' +
      'TREATMENT: α-blockade FIRST (phentolamine); nitroprusside or magnesium as adjuncts; rate control only after α-blockade. ' +
      'β-blockade BEFORE α-blockade is contraindicated — unopposed α-vasoconstriction drives BP even higher.',
    rhythm: 'st_depression',            // demand ischemia from the pressure surge
    correctInterventions: ['phentolamine', 'nitroprusside', 'magnesium'],
    contraindicated: ['metoprolol', 'labetalol'],
    channels: { abp: true },
    vitals: {
      HR: 135, SpO2: 96,
      NIBP_Sys: 240, NIBP_Dia: 130, ABP_Sys: 236, ABP_Dia: 128,
      EtCO2: 40, RR: 22, Temp: 37.8, CVP: 8,
    },
  },

  thyroidStorm: {
    name: 'Thyroid Storm',
    description:
      'Thyrotoxic crisis — hyperpyrexia (40 °C), AFib with rapid rate, high-output hypotension-in-evolution, agitation. ' +
      'TREATMENT: esmolol (β-blockade), corticosteroid (blocks T4→T3 + adrenal support), cooled IV fluids; ' +
      'antithyroid drugs and iodine follow (not modelled).',
    rhythm: 'afib',
    correctInterventions: ['esmolol', 'corticosteroid', 'ivFluid'],
    vitals: {
      HR: 165, SpO2: 95,
      NIBP_Sys: 145, NIBP_Dia: 65, ABP_Sys: 142, ABP_Dia: 62,   // wide pulse pressure
      EtCO2: 44, RR: 26, Temp: 40.2, CVP: 6,
    },
  },

  myxedemaComa: {
    name: 'Myxedema Coma',
    description:
      'Decompensated hypothyroidism — obtunded, bradycardic, hypothermic (33.5 °C), hypoventilating. ' +
      'TREATMENT: IV levothyroxine + empiric hydrocortisone (possible coexisting adrenal failure), ' +
      'active warming, warmed fluids, support/secure the airway. Rewarm slowly.',
    rhythm: 'hypothermia',              // bradycardia + Osborn (J) waves
    correctInterventions: ['levothyroxine', 'corticosteroid', 'bodyWarmer', 'warmedIVFluid', 'intubation'],
    channels: { temp: true },
    vitals: {
      HR: 42, SpO2: 90,
      NIBP_Sys: 85, NIBP_Dia: 50, ABP_Sys: 82, ABP_Dia: 48,
      EtCO2: 52, RR: 7, Temp: 33.5, CVP: 6,                     // CO₂ retention from hypoventilation
    },
  },

  adrenalCrisis: {
    name: 'Adrenal (Addisonian) Crisis',
    description:
      'Acute adrenal insufficiency — profound hypotension REFRACTORY TO PRESSORS until hydrocortisone is given, ' +
      'plus volume depletion and low-grade fever. TREATMENT: hydrocortisone + aggressive saline; ' +
      'pressors work only after steroid replacement restores vascular responsiveness.',
    correctInterventions: ['corticosteroid', 'ivFluid'],
    // Pressors achieve little until the steroid is on board and circulating.
    recoveryGate: {
      antidotes: ['corticosteroid'],
      mode: 'any',
      settleMs: 30000,
      blocks: ['phenylephrine', 'ephedrine', 'norepinephrine', 'epinephrine', 'vasopressin', 'dopamine'],
      hint: 'Pressor-refractory — give hydrocortisone (± fluids) and allow it to circulate.',
    },
    vitals: {
      HR: 118, SpO2: 96,
      NIBP_Sys: 72, NIBP_Dia: 42, ABP_Sys: 70, ABP_Dia: 40,
      EtCO2: 34, RR: 20, Temp: 37.9, CVP: 2,
    },
  },

  dka: {
    name: 'Diabetic Ketoacidosis',
    description:
      'DKA — Kussmaul hyperventilation compensating metabolic acidosis (low EtCO₂), tachycardia, volume depletion. ' +
      'TREATMENT: IV fluids FIRST, then insulin infusion; watch potassium as insulin shifts K⁺ intracellularly.',
    correctInterventions: ['ivFluid', 'insulinInfusion'],
    vitals: {
      HR: 125, SpO2: 97,
      NIBP_Sys: 95, NIBP_Dia: 55, ABP_Sys: 92, ABP_Dia: 52,
      EtCO2: 18, RR: 30, Temp: 37.2, CVP: 2,                    // deep, rapid breathing
    },
  },

  hypoglycemia: {
    name: 'Severe Hypoglycemia',
    description:
      'Neuroglycopenia under anesthesia — sympathetic surge (tachycardia, hypertension, sweating) is the only visible sign ' +
      'in an unconscious patient; untreated it progresses to seizures and arrest. TREATMENT: D50 IV bolus; recheck glucose.',
    correctInterventions: ['dextrose50'],
    vitals: {
      HR: 118, SpO2: 98,
      NIBP_Sys: 165, NIBP_Dia: 95, ABP_Sys: 162, ABP_Dia: 92,
      EtCO2: 36, RR: 18, Temp: 36.6, CVP: 5,
    },
  },

  carcinoidCrisis: {
    name: 'Carcinoid Crisis',
    description:
      'Massive mediator release (serotonin/histamine) — flushing, profound vasoplegic hypotension, bronchospasm. ' +
      'TREATMENT: octreotide IS the pressor here; fluids support. Catecholamines (epi/norepi) are contraindicated — ' +
      'they provoke further mediator release and worsen the hypotension.',
    capnoMorphology: 'obstructed',
    correctInterventions: ['octreotide', 'ivFluid'],
    contraindicated: ['epinephrine', 'norepinephrine'],
    vitals: {
      HR: 128, SpO2: 91,
      NIBP_Sys: 68, NIBP_Dia: 38, ABP_Sys: 65, ABP_Dia: 36,
      EtCO2: 50, RR: 24, Temp: 37.4, CVP: 3,
    },
  },

  serotoninSyndrome: {
    name: 'Serotonin Syndrome',
    description:
      'Serotonergic toxidrome (SSRI + fentanyl / methylene blue / MAOI) — RAPID onset (hours): clonus, hyperreflexia, ' +
      'agitation, hyperthermia 39.6 °C, autonomic surge. TREATMENT: stop serotonergic agents, benzodiazepines, ' +
      'cyproheptadine (specific antidote), active cooling, esmolol for autonomic instability. ' +
      'Fentanyl and methylene blue are serotonergic — giving them WORSENS the syndrome.',
    correctInterventions: ['cyproheptadine', 'midazolam', 'activeCooling', 'esmolol'],
    contraindicated: ['fentanyl', 'methyleneBlue'],
    channels: { temp: true, etco2: true },
    vitals: {
      HR: 132, SpO2: 97,
      NIBP_Sys: 165, NIBP_Dia: 95, ABP_Sys: 162, ABP_Dia: 92,
      EtCO2: 46, RR: 24, Temp: 39.6, CVP: 5,
    },
  },

  nms: {
    name: 'Neuroleptic Malignant Syndrome',
    description:
      'Dopamine blockade (antipsychotics / abrupt levodopa withdrawal) — SLOW onset (days): lead-pipe rigidity, ' +
      'hyperthermia 40 °C, autonomic instability, ↑CK, bradyreflexia (vs the clonus of serotonin syndrome). ' +
      'TREATMENT: stop the offending agent, dantrolene (rigidity/heat), bromocriptine (restores dopamine tone), ' +
      'benzodiazepines, active cooling.',
    correctInterventions: ['dantrolene', 'bromocriptine', 'midazolam', 'activeCooling'],
    channels: { temp: true, etco2: true },
    vitals: {
      HR: 125, SpO2: 95,
      NIBP_Sys: 150, NIBP_Dia: 85, ABP_Sys: 147, ABP_Dia: 82,
      EtCO2: 48, RR: 26, Temp: 40.0, CVP: 5,          // rigidity → hypoventilation, CO₂ climbs
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // CPR Quality States & Advanced Haemodynamic Support
  // These describe monitor appearances during active resuscitation, not
  // discrete disease presentations.  Activated via the Intervention panel.
  // ═══════════════════════════════════════════════════════════════════════

  cprHighQuality: {
    name: 'High Quality CPR',
    subtitle: 'EtCO₂ 10–20 · DBP > 20 mmHg',
    description:
      'Monitor appearance during high-quality CPR. ' +
      'ECG shows compression artifact (broad spikes at 100–120/min, not cardiac QRS). ' +
      'EtCO₂ 10–20 mmHg — primary quality metric; reflects pulmonary blood flow. ' +
      'ABP diastolic > 20 mmHg — target for adequate coronary perfusion pressure. ' +
      'SpO₂ probe unreliable (flat or artifact). NIBP cannot cycle. ' +
      'Abrupt EtCO₂ rise to 35–40 mmHg = earliest sign of ROSC.',
    rhythm: 'cpr_hq',
    capnoMorphology: 'normal',
    vitals: {
      HR: 110,          // displayed compression rate (100–120 = target range)
      SpO2: 40,         // probe unreliable during arrest — renders near-flat pleth
      NIBP_Sys: 0,   NIBP_Dia: 0,    // oscillometric cuff cannot cycle
      ABP_Sys: 55,   ABP_Dia: 25,    // diastolic 25 > 20 mmHg target — adequate CPP
      EtCO2: 15,        // within 10–20 mmHg high-quality range
      RR: 10,           // bag-mask ventilation rate (avoid hyperventilation)
      Temp: 36.0,
      CVP: 5,
    },
  },

  cprLowQuality: {
    name: 'Low Quality CPR',
    subtitle: 'EtCO₂ < 10 · DBP < 20 mmHg',
    description:
      'Monitor appearance during poor-quality CPR. ' +
      'ECG artifact at 80/min (too slow; target 100–120). Periodic pauses visible (poor CPR fraction). ' +
      'EtCO₂ < 10 mmHg — critical warning; compressions too shallow, too slow, or leaning on chest. ' +
      'ABP diastolic < 20 mmHg — coronary perfusion pressure inadequate. ' +
      'Actions: swap compressor, improve depth (≥ 5 cm), allow full chest recoil, reduce pauses.',
    rhythm: 'cpr_lq',
    capnoMorphology: 'normal',
    vitals: {
      HR: 80,           // compression rate too slow (< 100 bpm)
      SpO2: 40,
      NIBP_Sys: 0,   NIBP_Dia: 0,
      ABP_Sys: 28,   ABP_Dia: 8,     // diastolic 8 < 20 mmHg — below coronary perfusion threshold
      EtCO2: 6,         // < 10 mmHg — critically poor forward flow
      RR: 8,
      Temp: 36.0,
      CVP: 2,
    },
  },

  vaECMO: {
    name: 'V-A ECMO',
    subtitle: 'Non-pulsatile · MAP 65 · EtCO₂ ↓ (sweep)',
    description:
      'Veno-Arterial ECMO: continuous-flow circuit bypasses both heart and lungs. ' +
      'ABP waveform is nearly flat (non-pulsatile) — very narrow pulse pressure (MAP ≈ 65 mmHg). ' +
      'Target MAP 60–80 mmHg; systolic/diastolic goals no longer apply. ' +
      'EtCO₂ 10–20 mmHg — sweep gas removes CO₂ before it reaches the native lungs. ' +
      'CVP very low — ECMO drainage cannula pulls volume from right atrium. ' +
      'SpO₂ reliable from right hand only (Harlequin/North-South syndrome risk if native heart recovers). ' +
      'ECG shows underlying rhythm — VF or asystole possible despite maintained arterial pressure. ' +
      'Widening pulse pressure on ABP = native cardiac recovery (open aortic valve).',
    rhythm: 'normal',   // ECG reflects underlying cardiac state; adjust via scenario buttons separately
    capnoMorphology: 'normal',
    vitals: {
      HR: 72,
      SpO2: 99,         // ECMO oxygenates completely; probe on right hand per Harlequin protocol
      NIBP_Sys: 0,   NIBP_Dia: 0,    // oscillometric NIBP unreliable with non-pulsatile flow
      ABP_Sys: 70,   ABP_Dia: 62,    // narrow pulse pressure (8 mmHg) = non-pulsatile ECMO flow
      EtCO2: 12,        // sweep-gas-depleted CO₂ — little left for native lungs to exhale
      RR: 12,
      Temp: 37.0,
      CVP: 1,           // near-zero — ECMO drainage empties the right atrium
    },
  },

  lucasCPR: {
    name: 'Mechanical CPR (LUCAS)',
    subtitle: 'Machine compressions · 102/min · no fatigue',
    description:
      'Piston-driven mechanical compressions at a fixed 102/min — perfectly regular artifact with no ' +
      'compressor fatigue, no pauses and a constant depth. Frees hands for procedures and transport; ' +
      'EtCO₂ and diastolic pressure sit at the high-quality-CPR targets and STAY there.',
    rhythm: 'cpr_mech',
    capnoMorphology: 'normal',
    vitals: {
      HR: 102, SpO2: 40,
      NIBP_Sys: 0, NIBP_Dia: 0,
      ABP_Sys: 58, ABP_Dia: 26,      // consistent, fatigue-free coronary perfusion
      EtCO2: 18, RR: 10, Temp: 36.0, CVP: 5,
    },
  },

  iabp: {
    name: 'IABP 1:1',
    subtitle: 'Diastolic augmentation > systole · ↓afterload',
    description:
      'Intra-aortic balloon pump, 1:1 assist. Balloon inflates at the dicrotic notch → the DIASTOLIC ' +
      'augmentation peak is HIGHER than native systole (the IABP signature on the art line), boosting ' +
      'coronary perfusion; deflation just before systole lowers the assisted end-diastolic pressure ' +
      '(afterload reduction). For cardiogenic shock / post-cardiotomy support. NIBP is unreliable — ' +
      'follow the arterial line and MAP.',
    channels: { abp: true, cvp: true },
    vitals: {
      HR: 96, SpO2: 96,
      NIBP_Sys: 0, NIBP_Dia: 0,      // cuff cannot interpret augmented flow
      ABP_Sys: 92, ABP_Dia: 52,      // waveform shows augmentation above this systole
      EtCO2: 32, RR: 16, Temp: 36.6, CVP: 12,
    },
  },

  impella: {
    name: 'Impella CP',
    subtitle: 'Continuous axial flow · narrow pulse pressure',
    description:
      'Percutaneous axial-flow LVAD. Continuous forward flow partially unloads the LV → the arterial ' +
      'trace keeps a NARROW pulse pressure (≈15 mmHg) around a maintained MAP — between the native ' +
      'trace and the flat V-A ECMO line. Widening pulse pressure = native recovery; a suddenly flat ' +
      'trace = suction event or pump stop. NIBP unreliable; follow MAP.',
    channels: { abp: true, cvp: true },
    vitals: {
      HR: 92, SpO2: 96,
      NIBP_Sys: 0, NIBP_Dia: 0,
      ABP_Sys: 82, ABP_Dia: 67,      // PP 15 — partial unloading
      EtCO2: 33, RR: 15, Temp: 36.6, CVP: 11,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // NRP / PALS — 2020 AHA Core Case Scenarios
  // Vitals sourced directly from 2020 PALS Core Case Scenarios PDF.
  // These are intentionally higher HR / RR / lower BP than adult periop
  // norms — reflect paediatric physiology.
  // ═══════════════════════════════════════════════════════════════════════

  // ── Respiratory ─────────────────────────────────────────────────────────

  palsCroup: {
    name: 'Upper Airway Obstruction',
    // Nebulised epinephrine + corticosteroid (dexamethasone); humidified O₂;
    // minimise agitation.
    correctInterventions: ['epinephrine', 'corticosteroid', 'nonRebreather'],
    description:
      'Viral croup (9-month infant, 8 kg) — inspiratory stridor, subglottic oedema, SpO₂ 89%. ' +
      'Nebulized racemic epinephrine, dexamethasone 0.6 mg/kg IM/IV. High-flow humidified O₂. ' +
      'Avoid distressing the child — crying worsens obstruction.',
    // Upper-airway (subglottic) obstruction — ↑Ppeak, Pplat near-normal.
    vent: { vte: 90, ppeak: 34, pplat: 14 },
    // Spontaneously breathing infant — NOT intubated.
    intubated: false,
    vitals: {
      HR: 140, SpO2: 89,
      NIBP_Sys: 86,  NIBP_Dia: 58,
      ABP_Sys:  84,  ABP_Dia:  56,
      EtCO2: 45,   // slight CO₂ retention from partial obstruction
      RR: 48,
      Temp: 38.0,
      CVP: 3,
    },
  },

  palsAsthma: {
    name: 'Lower Airway Obstruction',
    // Albuterol (± ipratropium), systemic corticosteroid, magnesium; epinephrine
    // IM for severe/impending failure.
    correctInterventions: ['albuterol', 'corticosteroid', 'magnesium', 'epinephrine'],
    description:
      'Acute severe asthma (10-year-old, 35 kg) — SpO₂ 88%, EtCO₂ rising, silent chest an ominous sign. ' +
      'Salbutamol MDI/nebs + ipratropium, IV/IM magnesium 40 mg/kg (max 2 g), heliox. ' +
      'Intubation carries very high risk — last resort.',
    capnoMorphology: 'obstructed', // shark-fin from air trapping
    // Paediatric severe asthma — high resistance + air trapping (scaled VTe).
    vent: { vte: 230, ppeak: 42, pplat: 20 },
    vitals: {
      HR: 150, SpO2: 88,
      NIBP_Sys: 102, NIBP_Dia: 62,
      ABP_Sys:  100, ABP_Dia:  60,
      EtCO2: 58,   // air trapping drives EtCO₂ up
      RR: 30,
      Temp: 37.0,
      CVP: 5,
    },
  },

  palsPneumonia: {
    name: 'Lung Tissue Disease',
    // O₂ / positive-pressure support (PEEP), antibiotics for pneumonia, intubate if
    // failing.
    correctInterventions: ['nonRebreather', 'intubation', 'broadSpectrumAntibiotics'],
    description:
      'Severe pneumonia (3-year-old, 12 kg) — SpO₂ 80% failing to rise above 88% on 100% O₂ (shunt). ' +
      'HFNC or CPAP first; intubate if SpO₂ < 90% on non-rebreather. ' +
      'Antibiotics within 1 hour. PICU admission.',
    // Lung-tissue disease — low compliance: ↑Pplat (small PIP–plateau gap), ↓VTe.
    vent: { vte: 110, ppeak: 34, pplat: 30 },
    vitals: {
      HR: 160, SpO2: 80,
      NIBP_Sys: 110, NIBP_Dia: 78,
      ABP_Sys:  108, ABP_Dia:  76,
      EtCO2: 40,
      RR: 38,
      Temp: 38.5,
      CVP: 4,
    },
  },

  palsDisorderedBreathing: {
    name: 'Disordered Control of Breathing',
    // Support ventilation / intubate; reverse the cause — naloxone (opioid) or
    // flumazenil (benzodiazepine).
    correctInterventions: ['intubation', 'nonRebreather', 'naloxone', 'flumazenil'],
    description:
      'Post-ictal respiratory depression (4-year-old, 17 kg) — RR 8, SpO₂ 80%, CO₂ retention. ' +
      'Jaw thrust, OPA/NPA, bag-mask ventilation 100% O₂ → SpO₂ 99%. ' +
      'Benzodiazepines only if actively seizing. Identify fever source (febrile seizure).',
    vitals: {
      HR: 130, SpO2: 80,
      NIBP_Sys: 98,  NIBP_Dia: 62,
      ABP_Sys:  96,  ABP_Dia:  60,
      EtCO2: 55,   // CO₂ retention from hypoventilation
      RR: 8,
      Temp: 39.7,
      CVP: 4,
    },
  },

  // ── Shock ────────────────────────────────────────────────────────────────

  palsHypovolemicShock: {
    name: 'Hypovolemic Shock',
    // Rapid isotonic fluid boluses (20 mL/kg), blood for haemorrhage.
    correctInterventions: ['ivFluid', 'bloodTransfusion'],
    description:
      'Dehydration / fluid loss (5-year-old, 21 kg) — HR 140, BP 86/52, mottled skin, weak pulses, metabolic acidosis. ' +
      'IV/IO access × 2, isotonic 20 mL/kg bolus over 5–10 min × 3 if needed. ' +
      'Reassess after each bolus (HR, perfusion, mental status).',
    vitals: {
      HR: 140, SpO2: 97,
      NIBP_Sys: 86,  NIBP_Dia: 52,
      ABP_Sys:  84,  ABP_Dia:  50,
      EtCO2: 30,   // compensatory hyperventilation (metabolic acidosis)
      RR: 36,
      Temp: 38.0,
      CVP: 1,      // low circulating volume
    },
  },

  palsSepticShock: {
    name: 'Distributive / Septic Shock',
    description:
      'Septic shock (4-month infant, 5.1 kg) — HR 192, BP 76/30, capillary refill 5 s, lactate 5 mmol/L. ' +
      'IV/IO access, 20 mL/kg isotonic bolus (repeat × 3 if no improvement), broad-spectrum antibiotics within 1 hour. ' +
      'Norepinephrine for fluid-refractory shock.',
    vitals: {
      HR: 192, SpO2: 93,
      NIBP_Sys: 76,  NIBP_Dia: 30,
      ABP_Sys:  74,  ABP_Dia:  28,
      EtCO2: 30,   // compensatory hyperventilation from metabolic acidosis
      RR: 55,
      Temp: 39.0,
      CVP: 1,
    },
  },

  palsCardiogenicShock: {
    name: 'Cardiogenic Shock',
    // Inotropy (dobutamine/epinephrine) + afterload reduction/diuresis; cautious,
    // SMALL fluid aliquots only — large boluses worsen pulmonary oedema.
    correctInterventions: ['dobutamine', 'epinephrine', 'norepinephrine', 'furosemide'],
    description:
      'Myocarditis → cardiogenic shock (16-year-old, 82 kg) — HR 140, BP 86/40, bilateral crackles, JVD, cardiomegaly, CVP ↑↑. ' +
      'Cautious fluids (risk of flash pulmonary oedema). Dopamine or epinephrine. ' +
      'Consider ECMO for refractory shock; urgent cardiology consult.',
    vitals: {
      HR: 140, SpO2: 89,
      NIBP_Sys: 86,  NIBP_Dia: 40,
      ABP_Sys:  84,  ABP_Dia:  38,
      EtCO2: 30,   // low CO with metabolic acidosis
      RR: 35,
      Temp: 37.0,
      CVP: 14,     // venous congestion — RV failure / fluid overload
    },
  },

  palsTensionPneumo: {
    name: 'Obstructive Shock',
    // Needle/finger decompression is definitive; support with 100% O₂ + fluids.
    correctInterventions: ['needleDecompression', 'ivFluid', 'nonRebreather'],
    description:
      'Tension pneumothorax (8-year-old, 20 kg, intubated) — SpO₂ 68% on 100% O₂, absent right breath sounds, ↑airway pressures, CVP ↑. ' +
      'Needle decompression 2nd ICS MCL immediately (large-bore IV catheter). ' +
      'Follow with chest tube. DOPE mnemonic: Dislodgement, Obstruction, Pneumothorax, Equipment.',
    // Tension pneumothorax (intubated) — ↑↑ airway pressures, collapsed VTe.
    vent: { vte: 70, ppeak: 52, pplat: 46 },
    vitals: {
      HR: 140, SpO2: 68,
      NIBP_Sys: 80,  NIBP_Dia: 54,
      ABP_Sys:  78,  ABP_Dia:  52,
      EtCO2: 8,    // near-flat — lungs ventilated but no pulmonary blood flow
      RR: 0,       // intubated — no spontaneous effort
      Temp: 37.2,
      CVP: 10,     // elevated from mediastinal shift / obstructed venous return
    },
  },

  // ── Arrhythmia ───────────────────────────────────────────────────────────

  palsSvtStable: {
    name: 'SVT — Stable',
    description:
      'SVT with adequate perfusion (12-year-old, 44 kg) — HR 235, narrow complex, BP 100/65. ' +
      'Vagal manoeuvres first (ice bag to face, Valsalva). ' +
      'Adenosine 0.1 mg/kg IV rapid push (max 6 mg); 0.2 mg/kg (max 12 mg) if no conversion.',
    rhythm: 'svt',
    vitals: {
      HR: 235, SpO2: 94,
      NIBP_Sys: 100, NIBP_Dia: 65,
      ABP_Sys:   98, ABP_Dia:  63,
      EtCO2: 36,
      RR: 20,
      Temp: 37.6,
      CVP: 6,
    },
  },

  palsSvtUnstable: {
    name: 'SVT — Unstable',
    // Unstable → synchronised cardioversion 0.5–1 J/kg. Adenosine only if IV access
    // is already in and it will not delay the shock.
    correctInterventions: ['synchronizedCardioversion', 'adenosine'],
    description:
      'SVT with haemodynamic compromise (3-month infant, 5 kg) — HR 235, BP 50/32, signs of heart failure. ' +
      'Synchronised cardioversion 0.5–1 J/kg (sedate if time allows, do not delay). ' +
      'Adenosine acceptable if IV access immediately available and no delay to shock.',
    rhythm: 'svt',
    vitals: {
      HR: 235, SpO2: 92,
      NIBP_Sys: 50,  NIBP_Dia: 32,
      ABP_Sys:  48,  ABP_Dia:  30,
      EtCO2: 28,   // poor CO
      RR: 60,
      Temp: 37.6,
      CVP: 9,      // venous congestion from backward heart failure
    },
  },

  // ── Cardiac Arrest ───────────────────────────────────────────────────────

  palsVfArrest: {
    name: 'VF Arrest',
    // Defibrillate (2→4 J/kg) + CPR; epinephrine q3–5 min; amiodarone or lidocaine
    // for refractory VF/VT.
    correctInterventions: ['epinephrine', 'amiodarone', 'lidocaine'],
    description:
      'Pulseless ventricular fibrillation (7-year-old, 25 kg) — shockable rhythm. ' +
      'Defibrillate 2 J/kg → CPR 2 min → reassess. Epinephrine 0.01 mg/kg IV/IO after 2nd shock. ' +
      'Amiodarone 5 mg/kg (or lidocaine 1 mg/kg) for refractory VF.',
    rhythm: 'vf',
    vitals: {
      HR: 0,   SpO2: 40,
      NIBP_Sys: 0,   NIBP_Dia: 0,
      ABP_Sys:  0,   ABP_Dia:  0,
      EtCO2: 0,   RR: 0,
      Temp: 36.0,  CVP: 0,
    },
  },

  palsAsystole: {
    name: 'Asystole',
    // High-quality CPR + epinephrine q3–5 min; treat reversible causes.
    correctInterventions: ['epinephrine'],
    description:
      'Cardiac arrest — asystole (6-year-old, 23 kg; submersion). Non-shockable rhythm. ' +
      'High-quality CPR immediately (100–120/min, 30:2 or 15:2 with 2 rescuers). ' +
      'Epinephrine 0.01 mg/kg IV/IO q3–5 min. Search for and treat H\'s & T\'s.',
    rhythm: 'asystole',
    vitals: {
      HR: 0,   SpO2: 40,
      NIBP_Sys: 0,   NIBP_Dia: 0,
      ABP_Sys:  0,   ABP_Dia:  0,
      EtCO2: 0,   RR: 0,
      Temp: 36.0,  CVP: 0,
    },
  },

  palsPEA: {
    name: 'Pulseless Elec. Activity',
    // CPR + epinephrine q3–5 min; find & treat the cause (fluids for hypovolaemia, etc.).
    correctInterventions: ['epinephrine', 'ivFluid'],
    description:
      'PEA — organised ECG, no palpable pulse (9-month infant, 8 kg; sepsis). Non-shockable. ' +
      'CPR immediately. Epinephrine 0.01 mg/kg IV/IO q3–5 min. ' +
      'Treat reversible causes: Hypovolemia, Hypoxia, H⁺, Hypo/Hyperkalemia, Hypothermia, ' +
      'Tension pneumo, Tamponade, Toxins, Thrombosis.',
    // Organised sinus tachycardia visible on ECG — no mechanical output
    rhythm: 'normal',
    vitals: {
      HR: 120,  SpO2: 40,
      NIBP_Sys: 0,   NIBP_Dia: 0,
      ABP_Sys:  0,   ABP_Dia:  0,
      EtCO2: 0,   RR: 0,
      Temp: 36.0,  CVP: 0,
    },
  },

  palsBradycardiaArrest: {
    name: 'Bradycardia / CPF',
    // Oxygenate/ventilate first; CPR if HR < 60 with poor perfusion; epinephrine;
    // atropine for vagal/AV block; consider pacing.
    correctInterventions: ['epinephrine', 'atropine', 'transcutaneousPacemaker', 'intubation'],
    description:
      'Cardiopulmonary failure from hypoxia (3-month infant, 5.7 kg; bronchiolitis) — HR 45, RR 4, no obtainable BP or SpO₂. ' +
      'Open airway + bag-mask ventilation 100% O₂ immediately. ' +
      'If HR < 60 with poor perfusion after 30 s ventilation → begin CPR. ' +
      'Epinephrine 0.01 mg/kg IV/IO. If vagal tone suspected: atropine 0.02 mg/kg IV (min 0.1 mg).',
    // Sinus bradycardia — rate-correct ECG generation at HR=45
    vitals: {
      HR: 45,   SpO2: 40,
      NIBP_Sys: 50,  NIBP_Dia: 28,
      ABP_Sys:  0,   ABP_Dia:  0,   // no reliable arterial pulse
      EtCO2: 5,    RR: 4,
      Temp: 39.6,
      CVP: 2,
    },
  },

  anaphylaxis: {
    name: 'Anaphylaxis',
    description: 'IgE-mediated anaphylaxis — distributive shock + bronchospasm. Epinephrine 0.5 mg IM FIRST-LINE. Fluid resuscitation, antihistamines, steroids, airway management.',
    // Bronchospasm is a cardinal feature — obstructed capnogram auto-applies
    capnoMorphology: 'obstructed',
    // Severe bronchospasm — very high airway resistance: Ppeak ↑↑↑, VTe ↓↓.
    vent: { vte: 320, ppeak: 50, pplat: 24 },
    vitals: {
      // Marked compensatory tachycardia + direct histamine chronotropy
      HR: 148,
      // Hypoxia from bronchospasm, airway edema, and ventilation-perfusion mismatch
      SpO2: 86,
      // Profound distributive shock — massive histamine-driven vasodilation drops SVR to near zero
      NIBP_Sys: 58,  NIBP_Dia: 30,
      ABP_Sys:  55,  ABP_Dia:  28,
      // Obstructed exhalation from bronchospasm raises EtCO₂
      EtCO2: 60,
      // Tachypnoea driven by hypoxia + bronchospasm work of breathing
      RR: 30,
      // Slight pyrexia from histamine-mediated vasodilation/flush
      Temp: 37.5,
      // Very low CVP — vasodilation collapses venous return; "empty tank" physiology
      CVP: 1,
    },
  },
};

// Attach cognitive-aid + investigation cards to the existing core obstetric
// scenarios so the whole OB/GYN tab supports push-to-monitor sub-buttons.
for (const [key, extra] of Object.entries(OB_AID_ATTACH)) {
  if (SCENARIOS[key]) Object.assign(SCENARIOS[key], extra);
}
